/* eslint-disable */
import { useState } from 'react';
import { Handle } from 'react-flow-renderer';
import { useHistory } from 'react-router-dom';
import {
    Globe, CheckCheck, List, ShoppingCart, Box, Download, Edit2, Trash2, Eye,
    AlertTriangle, Activity, Clock, FileText, Database, TrendingUp, TrendingDown, Info, ChevronDown,
    CookingPot,
    CheckCircle2,
    AlertCircle,
    RotateCcw,
    PackageOpen,
    Truck
} from 'lucide-react';
import machine from '../../../Images/machine.svg';
import storageIcon from '@/Images/store.png';
import operationIcon from '@/Images/operation.png';
import { modes, getHigherUnitAndQuantity } from '@/Helpers';
import { useAuth } from '@/Components/Auth/Login/auth';
import issue from '@/Images/issued.svg';
import restaurantIssue from '@/Images/res_issued.svg';
import produce from '@/Images/produced.svg';
import dispatch from '@/Images/externalDispatch.svg';
import internalDispatch from '@/Images/internal dispatch.svg';
import wastage from '@/Images/wastage.svg';
import phone from '@/Images/phone.png';
import list from '@/Images/ProudctionList.svg';
import yeildLossActual from '@/Images/yeildLossActual.png';
import yeildLossExpected from '@/Images/yeildLossExpected.png';
import ega from '@/Images/ega.svg';
import secondaryPack from '@/Images/secondaryPack.svg';
import primaryPack from '@/Images/primaryPack.svg';
import { MODERN_NODE_CONFIGS } from '@/config/modernNodeConfig';

// Procured = Transfer In: a truck mirrored to face inward (opposite of the
// right-facing Dispatch truck), so the two read as an in/out pair.

const TransferOutIcon = ({ className = '', ...rest }) => (
    <img src={internalDispatch} className={`${className} -scale-x-100`} {...rest} />
);

const DispatchRightIcon = ({ className = '', ...rest }) => (
    <img src={dispatch} className={`${className} -scale-x-100`} {...rest} />
);

function ModernNode(props) {
    const {
        data,
        id,
        poActionHandler,
        onDelete,
        handleAssetUpdate,
        handleAssetView,
        context = 'processFlow',
        customConfig = {},
        renderHeader = null,
        renderMetrics = null,
        renderDefaultMode = null,
        renderWorkOrders = null,
        renderRecipeStats = null,
        renderActions = null,
        isProductJourney = false,
    } = props;

    const [isStatsExpanded, setIsStatsExpanded] = useState(false);

    const { userConfig = {} } = useAuth();
    const { level } = userConfig?.userDetails || {};
    const { userRolesDict = {} } = userConfig || {};
    const isInternal = Object?.values?.(userRolesDict ?? {}).includes('INTERNAL') ||
        Object?.values?.(userRolesDict ?? {}).includes('Internal');

    const history = useHistory();

    const handleNodeClick = () => {
        const link = data?.wholeApi?.link;
        if (link) {
            window.open(link, '_blank');
        }
    };

    const config = {
        ...MODERN_NODE_CONFIGS[context],
        ...customConfig,
        header: {
            ...MODERN_NODE_CONFIGS[context]?.header,
            ...customConfig?.header,
        },
        metrics: {
            ...MODERN_NODE_CONFIGS[context]?.metrics,
            ...customConfig?.metrics,
        },
        mode: {
            ...MODERN_NODE_CONFIGS[context]?.mode,
            ...customConfig?.mode,
        },
        workOrders: {
            ...MODERN_NODE_CONFIGS[context]?.workOrders,
            ...customConfig?.workOrders,
        },
        actions: {
            ...MODERN_NODE_CONFIGS[context]?.actions,
            ...customConfig?.actions,
        },
        recipeStats: {
            ...MODERN_NODE_CONFIGS[context]?.recipeStats,
            ...customConfig?.recipeStats,
        }
    };

    const getMachineStatus = (status) => {
        const normalizedStatus = status?.toLowerCase();
        const statusConfig = {
            low: { bg: 'bg-orange-200', text: 'text-orange-700', label: 'Low Usage' },
            idle: { bg: 'bg-red-200', text: 'text-red-700', label: 'Idle' },
            active: { bg: 'bg-green-200', text: 'text-green-700', label: 'Active' },
            offline: { bg: 'bg-gray-200', text: 'text-gray-700', label: 'Offline' },
        };

        const statusData = statusConfig[normalizedStatus] || {
            bg: 'bg-gray-200',
            text: 'text-gray-700',
            label: status || 'Unknown'
        };

        const iconSize = context === 'processFlow' ? 80 : 40;
        const fontSize = config.mode?.fontSize || 'text-base';

        return (
            <div className={`w-full py-5 rounded-2xl flex items-center justify-center gap-4 ${fontSize} font-black uppercase shadow-sm border-2 border-black/5 ${statusData.bg} ${statusData.text}`}>
                <Globe size={iconSize} strokeWidth={2.5} />
                {statusData.label}
            </div>
        );
    };

    const getMetricInfo = (key) => {
        const trimmedKey = key?.toLowerCase()?.trim() || '';
        if (trimmedKey.includes('issuance')) return { icon: issue, rank: 1 };
        if (trimmedKey.includes('procured')) return { icon: internalDispatch, rank: 2 };
        if (trimmedKey.includes('transfer out')) return { icon: TransferOutIcon, rank: 3 };
        if (trimmedKey.includes('sfg')) return { icon: produce, rank: 4 };
        if (trimmedKey.includes('produced')) return { icon: produce, rank: 5 };
        if (trimmedKey.includes('primary')) return { icon: primaryPack, rank: 6 };
        if (trimmedKey.includes('ega')) return { icon: ega, rank: 7 };
        if (trimmedKey.includes('yeildlossactual')) return { icon: yeildLossActual, rank: 8 };
        if (trimmedKey.includes('yeildlossexpected')) return { icon: yeildLossExpected, rank: 9 };
        if (trimmedKey.includes('secondary')) return { icon: secondaryPack, rank: 10 };
        if (trimmedKey.includes('wastage')) return { icon: wastage, rank: 11 };
        if (trimmedKey.includes('leftover')) return { icon: RotateCcw, rank: 12 };
        if (trimmedKey.includes('dispatch')) return { icon: DispatchRightIcon, rank: 13 };
        if (trimmedKey.includes('restaurant') || trimmedKey.includes('indent')) return { icon: restaurantIssue, rank: 14 };
        return { icon: ShoppingCart, rank: 99 };
    };

    const getBarColor = (percentage) => {
        if (percentage >= 80) return 'bg-teal-500';
        if (percentage >= 40) return 'bg-orange-500';
        return 'bg-red-500';
    };

    // ==================== RENDER SECTIONS ====================
    const defaultRenderHeader = () => {
        const { iconSize, titleSize, subtitleSize, layout } = config.header;
        const productName = data?.wholeApi?.productName;
        const batchName = data?.wholeApi?.batchName;

        return (
            <div className='px-10 pt-10 pb-8'>
                {/* Product Name and Batch Name Header - Only show when product journey is selected */}
                {isProductJourney && (productName || batchName) && (
                    <div className='mb-6 pb-6 border-b-2 border-orange-200/50'>
                        {productName && (
                            <h2 className='text-[6rem] font-black text-indigo-800 leading-tight tracking-tight mb-2'>
                                {productName}
                            </h2>
                        )}
                        {batchName && (
                            <p className='text-[5.5rem] text-gray-600 font-semibold'>
                                Batch: <span className='font-bold text-gray-800'>{batchName}</span>
                            </p>
                        )}
                    </div>
                )}
                <div className={`flex ${layout === 'horizontal' ? 'flex-row' : 'flex-col'} gap-6 items-start`}>
                    <div className='p-5 shadow-md backdrop-blur-md'>
                        <img
                            className={iconSize}
                            src={data?.type === 'STORAGE' ? storageIcon : operationIcon}
                            alt={data?.type}
                        />
                    </div>
                    <div className='flex-1'>
                        <h3 className={`font-bold text-gray-900 ${titleSize} leading-tight tracking-tight`}>
                            {data?.heading}
                        </h3>
                        <p className={`${subtitleSize} text-gray-500 font-bold uppercase tracking-widest mt-3 flex items-center gap-2`}>
                            <span
                                className='w-8 h-8 rounded-full'
                                style={{ backgroundColor: data?.sectionColor }}
                            ></span>
                            {data?.sectionName}
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    const defaultRenderDefaultMode = () => {
        if (!config.showDefaultMode || !data?.defaultMode) return null;
        const { fontSize } = config.mode;

        return (
            <div className='px-10 mb-8'>
                <div className='w-full py-6 rounded-3xl bg-blue-50/50 border-[3px] border-blue-100 flex items-center justify-center gap-6 shadow-sm'>
                    <span className={`${fontSize} font-bold text-blue-400 uppercase tracking-widest`}>
                        Default Mode
                    </span>
                    <span className='w-4 h-4 rounded-full bg-blue-300'></span>
                    <span className={`${fontSize} font-black text-blue-900 uppercase tracking-widest`}>
                        {modes?.[data?.defaultMode]}
                    </span>
                </div>
            </div>
        );
    };

    const defaultRenderMetrics = () => {
        if (!config.showMetrics || !data?.stats?.displayValue) return null;

        const { iconSize, percentSize, valueSize, layout, showProgressBar } = config.metrics;
        const isHorizontal = layout === 'horizontal';

        const sortedKeys = Object.keys(data.stats.displayValue).sort((a, b) => getMetricInfo(a).rank - getMetricInfo(b).rank);

        return (
            <div className={`px-${context === 'processFlow' ? '10' : '4'} flex flex-col gap-${context === 'processFlow' ? '6' : '3'} mb-${context === 'processFlow' ? '10' : '4'}`}>
                {sortedKeys.map((ele, index) => {
                    const trimmedKey = ele?.trim();
                    const percentValue = data?.stats?.percentageValue?.[trimmedKey] ?? 0;
                    const percentage = parseFloat(percentValue) || 0;
                    const [val1, val2] = data.stats.displayValue[ele].split("/");
                    const Icon = getMetricInfo(ele)?.icon;
                    const barColor = getBarColor(percentage);

                    return (
                        <div key={index} className={`bg-white/60 border-2 border-orange-100 rounded-2xl p-6 shadow-sm`}>
                            <div className={`flex ${isHorizontal ? 'flex-row' : 'flex-col'} items-center gap-6 mb-3`}>
                                <div className='p-3 bg-gray-50 rounded-xl flex-shrink-0'>
                                    {typeof Icon === 'string' ? (
                                        <img src={Icon} className={`${iconSize} text-gray-700`} alt={ele} />
                                    ) : (
                                        <Icon className={`${iconSize} text-gray-700`} aria-label={ele} />
                                    )}
                                </div>
                                {showProgressBar && (
                                    <div className={`${isHorizontal ? 'flex-1' : 'w-full'} h-16 bg-gray-100 rounded-full overflow-hidden shadow-inner`}>
                                        <div
                                            className={`h-full rounded-full ${barColor} transition-all duration-500`}
                                            style={{ width: `${Math.min(percentage, 100)}%` }}
                                        />
                                    </div>
                                )}
                                <span className={`${percentSize} font-black text-gray-800 ${isHorizontal ? 'min-w-[6rem] text-right flex-shrink-0' : 'text-center w-full'} whitespace-nowrap`}>
                                    {percentage}%
                                </span>
                            </div>
                            <div className='text-gray-500/90 text-center font-bold font-mono tracking-tight pl-16'>
                                <span className={`${valueSize} text-gray-600`}>{val1}</span>
                                {val2 && (
                                    <span className={`${valueSize} text-gray-500/90`}>/{val2}</span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const defaultRenderRecipeStats = () => {
        if (!config.showRecipeStats) return null;
        const { titleSize, labelSize, valueSize } = config.recipeStats || {};
        const { recipeStats } = data?.wholeApi || {};
        if (!recipeStats) return null;

        const formatWeight = (val) => {
            if (val === undefined || val === null) return '0.000kg';
            return (val / 1000).toFixed(3) + 'kg';
        };

        const StatBox = ({ children, title, icon: Icon, colorClass, borderColor }) => (
            <div className={`bg-white/90 backdrop-blur-sm rounded-3xl p-6 border-2 ${borderColor} shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full w-full`}>
                <div className='flex items-center gap-4 mb-4'>
                    <Icon className={`w-20 h-20 ${colorClass}`} />
                    <span className={`${labelSize} font-bold text-gray-700 tracking-tight uppercase`}>{title}</span>
                </div>
                <div className="flex-1">
                    {children}
                </div>
            </div>
        );

        return (
            <div className={`px-${context === 'processFlow' ? '10' : '4'} mb-8`}>
                <div className='bg-orange-50/50 border-2 border-orange-200 rounded-3xl p-8 shadow-sm'>
                    <div
                        className='flex items-center justify-between cursor-pointer group'
                        onClick={(e) => { e.stopPropagation(); setIsStatsExpanded(!isStatsExpanded); }}
                    >
                        <h4 className={`${titleSize} font-bold text-orange-900 flex items-center gap-4`}>
                            <CookingPot className='w-[100px] h-[100px]' /> Recipe Stats
                        </h4>
                        <ChevronDown
                            size={80}
                            className={`text-orange-900 transition-transform duration-300 ${isStatsExpanded ? 'rotate-180' : ''}`}
                        />
                    </div>

                    <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isStatsExpanded ? 'max-h-[3500px] opacity-100 mt-8' : 'max-h-0 opacity-0'}`}>
                        <div className='flex flex-col gap-10'>
                            {/* Steps Row */}
                            <StatBox
                                title="Steps"
                                icon={CheckCircle2}
                                colorClass="text-green-600"
                                borderColor="border-green-100"
                            >
                                <div className='flex items-center justify-between gap-12'>
                                    <div className={`${valueSize} font-black leading-none`}>
                                        {recipeStats?.totalStepsExecuted || 0}/{recipeStats?.totalSteps || 0}
                                    </div>
                                    <div className='flex gap-10'>
                                        <div className='flex items-center gap-3 text-red-500 font-bold'>
                                            <TrendingUp className='w-16 h-16' />
                                            <span className='text-[3.8rem] whitespace-nowrap'>Over: {recipeStats?.overWeightSteps || 0}</span>
                                        </div>
                                        <div className='flex items-center gap-3 text-orange-500 font-bold'>
                                            <TrendingDown className='w-16 h-16' />
                                            <span className='text-[3.8rem] whitespace-nowrap'>Under: {recipeStats?.underWeightSteps || 0}</span>
                                        </div>
                                    </div>
                                </div>
                            </StatBox>

                            {/* Target Weight Row */}
                            <StatBox
                                title="Weight"
                                icon={TrendingUp}
                                colorClass="text-blue-600"
                                borderColor="border-blue-100"
                            >
                                <div className='flex flex-col gap-6'>
                                    <div className='flex items-center justify-between'>
                                        <div className='flex flex-col'>
                                            <span className='text-[3.2rem] text-gray-500 font-bold uppercase'>Target</span>
                                            <span className={`${valueSize} font-black leading-none`}>
                                                {formatWeight(recipeStats?.poQuantityWeight)}
                                            </span>
                                        </div>
                                        <div className='flex flex-col items-end'>
                                            <span className='text-[3.2rem] text-gray-500 font-bold uppercase'>Measured</span>
                                            <div className='flex items-center gap-3 font-black'>
                                                <Activity className='w-16 h-16' />
                                                <span className={`${valueSize} leading-none`}>{formatWeight(recipeStats?.fulfilledWeight)}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='flex items-center align-center gap-10 pt-4 border-t border-blue-50'>
                                        <div className='flex items-center gap-4'>
                                            <TrendingUp className='w-16 h-16 text-red-600' />
                                            <span className='text-[3.2rem] text-red-600 font-bold uppercase'>Over (W):</span>
                                            <span className='text-[3.8rem] text-red-600 font-black'>+{formatWeight(recipeStats?.overWeight)}</span>
                                        </div>
                                        <div className='flex items-center gap-4'>
                                            <TrendingDown className='w-16 h-16 text-orange-600' />
                                            <span className='text-[3.2rem] text-orange-600 font-bold uppercase'>Under (W):</span>
                                            <span className='text-[3.8rem] text-orange-600 font-black'>-{formatWeight(recipeStats?.underWeight)}</span>
                                        </div>
                                    </div>
                                </div>
                            </StatBox>

                            {/* Kit Issued Row */}
                            <StatBox
                                title="Kit Issued"
                                icon={Box}
                                colorClass="text-indigo-600"
                                borderColor="border-indigo-100"
                            >
                                <div className='flex items-center justify-between'>
                                    <div className='flex flex-col'>
                                        <span className='text-[3.2rem] text-gray-500 font-bold uppercase'>Kits Processed</span>
                                        <span className={`${valueSize} font-black leading-none mt-2`}>
                                            {recipeStats?.issuedKits || 0}/{recipeStats?.totalKits || 0}
                                        </span>
                                    </div>
                                </div>
                            </StatBox>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const defaultRenderWorkOrders = () => {
        if (!config.showWorkOrders || (data?.pending < 0 && data?.completed < 0)) return null;
        const { fontSize, iconSize } = config.workOrders;

        return (
            <div className='px-10 py-6 flex justify-between items-center hover:bg-[#FFE0B2]/20 transition-colors'>
                <span className={`${fontSize} font-bold`}>Work Orders</span>
                <div className={`flex items-center gap-3 ${fontSize} font-bold text-teal-700 px-5 py-2`}>
                    <CheckCheck size={iconSize} />
                    <span>{data?.completed}/{((data?.completed) + (data?.pending)) || 0}</span>
                </div>
            </div>
        );
    };

    const defaultRenderActions = () => {
        if (!config.showActions) return null;
        const { iconSize } = config.actions;

        const hasOrderData = data?.orderId || data?.wholeApi?.procurePoSummary || data?.wholeApi?.dispatchPoSummary;
        if (!hasOrderData && (!isInternal || level === 1)) return null;

        return (
            <div className='px-10 py-6 flex justify-around items-center text-gray-400 bg-white/40'>
                {hasOrderData && (
                    <>
                        {(data?.orderId || data?.cmsPoId) && (
                            <img src={list} onClick={(e) => { e.stopPropagation(); poActionHandler(data); }} className='w-[120px] h-[120px]text-black hover:text-gray-900 cursor-pointer transition-all hover:scale-110' />
                        )}
                        {data?.wholeApi?.procurePoSummary && (
                            <ShoppingCart
                                size={iconSize}
                                className='text-black hover:text-blue-600 cursor-pointer transition-all hover:scale-110'
                                onClick={(e) => { e.stopPropagation(); poActionHandler(data, 3); }}
                            />
                        )}
                        {data?.wholeApi?.dispatchPoSummary && (
                            <img src={dispatch} className='w-[140px] h-[140px] -scale-x-100 text-black hover:text-orange-600 cursor-pointer transition-all hover:scale-110' onClick={(e) => { e.stopPropagation(); poActionHandler(data, 4); }} />
                        )}
                        {(data?.orderId || data?.cmsPoId) && (
                            <Download
                                size={iconSize}
                                className='text-black hover:text-red-500 cursor-pointer transition-all hover:scale-110'
                                onClick={(e) => { e.stopPropagation(); poActionHandler(data, 2); }}
                            />
                        )}
                    </>
                )}
                {(isInternal && level !== 1) && (
                    <>
                        <Edit2
                            size={iconSize * 0.8}
                            className='text-black hover:text-blue-600 cursor-pointer transition-all hover:scale-110'
                            onClick={(e) => { e.stopPropagation(); handleAssetUpdate && handleAssetUpdate({ id, ...data }); }}
                        />
                        <Trash2
                            size={iconSize * 0.8}
                            className='text-black hover:text-red-600 cursor-pointer transition-all hover:scale-110'
                            onClick={(e) => { e.stopPropagation(); onDelete && onDelete({ id, ...data }); }}
                        />
                    </>
                )}
            </div>
        );
    };

    return (
        <>
            {config.showHandles && (
                <>
                    <Handle type='target' position='top' style={{ width: '40px', height: '40px', zIndex: 99 }} id={`${id}-top`} />
                    <Handle type='target' position='left' style={{ width: '40px', height: '40px', zIndex: 99 }} id={`${id}-left`} />
                </>
            )}

            <div
                className={`relative h-fit ${config.borderRadius} ${config.padding} shadow-[0_10px_40px_-10px_rgba(0,0,0,0.2)] border-4 border-orange-100 font-sans transform transition-all ${config.enableHover ? 'hover:scale-105' : ''} duration-200`}
                style={{ backgroundColor: data?.bgColor || '#FFF5E0', width: config.cardWidth, cursor: data?.wholeApi?.link ? 'pointer' : 'default' }}
                onClick={handleNodeClick}
            >
                {/* Phone Icon */}
                {config.showPhone && data?.hasResoeeApp > 0 && (
                    <div className='absolute -top-[22rem] -left-[10rem] w-[30rem] h-[30rem] z-[100] transition-transform hover:-translate-y-4'>
                        <img src={phone} alt='phone' className='w-full h-full object-contain drop-shadow-2xl' />
                    </div>
                )}

                {/* Machine Icon */}
                {config.showMachine && data?.totalMachines > 0 && (
                    <div className={`absolute ${context === 'processFlow' ? '-top-[20rem] -right-[7rem] w-[30rem] h-[30rem]' : '-top-[8rem] -right-[3rem] w-[12rem] h-[12rem]'} z-[100] transition-transform hover:-translate-y-4`}>
                        <img src={machine} alt='Machine' className='w-full h-full object-contain drop-shadow-2xl' />
                        <div className={`absolute ${context === 'processFlow' ? 'top-[1rem] right-[14rem] w-[8.5rem] h-[8.5rem] text-[7rem]' : 'top-[0.5rem] right-[5rem] w-[3.5rem] h-[3.5rem] text-[2rem]'} bg-white rounded-full flex items-center justify-center font-bold shadow-xl border-2 border-gray-100 text-black`}>
                            {data.totalMachines}
                        </div>
                    </div>
                )}

                {/* Header */}
                {renderHeader ? renderHeader() : defaultRenderHeader()}

                {/* Default Mode */}
                {renderDefaultMode ? renderDefaultMode() : defaultRenderDefaultMode()}

                {/* Machine Status */}
                {config.showMachineStatus && data?.totalMachines > 0 && (
                    <div className={`px-${context === 'processFlow' ? '10' : '4'} mb-${context === 'processFlow' ? '10' : '4'}`}>
                        {getMachineStatus(data?.machineStatus)}
                    </div>
                )}

                {/* Asset Info */}
                {config.showAsset && data?.asset?.assetName && (
                    <div className={`px-${context === 'processFlow' ? '10' : '4'} mb-${context === 'processFlow' ? '8' : '4'}`}>
                        <div className='w-full py-6 rounded-3xl bg-indigo-50/80 border-[3px] border-indigo-200 flex items-center justify-between px-8 shadow-sm group hover:border-indigo-300 transition-colors'>
                            <div className='flex flex-col'>
                                <span className='text-[2rem] font-bold text-indigo-400 uppercase tracking-widest mb-1'>Asset</span>
                                <span className='text-[2.5rem] font-black text-indigo-900 uppercase tracking-tight'>
                                    {data.asset.assetName}
                                    {data.asset.assetTypeName && <span className='text-indigo-400 text-[2.5rem] ml-4 font-bold'>({data.asset.assetTypeName})</span>}
                                </span>
                            </div>
                            <Eye size={80} onClick={(e) => { e.stopPropagation(); handleAssetView(data); }} className='cursor-pointer' />
                        </div>
                    </div>
                )}

                {/* Metrics */}
                {renderMetrics ? renderMetrics() : defaultRenderMetrics()}

                {/* Recipe Stats */}
                {renderRecipeStats ? renderRecipeStats() : defaultRenderRecipeStats()}

                {/* Footer */}
                <div className='border-t-2 border-orange-100 rounded-b-[2rem] overflow-hidden backdrop-blur-md'>
                    {renderWorkOrders ? renderWorkOrders() : defaultRenderWorkOrders()}
                    {config.showWorkOrders && config.showActions && (
                        <div className='h-0.5 bg-orange-200/40 w-full'></div>
                    )}
                    {renderActions ? renderActions() : defaultRenderActions()}
                </div>
            </div>

            {config.showHandles && (
                <>
                    <Handle
                        type='source'
                        position='bottom'
                        style={{ width: '40px', height: '40px', zIndex: 99, opacity: isInternal ? 0.25 : 1 }}
                        id={`${id}-bottom`}
                    />
                    <Handle
                        type='source'
                        position='right'
                        style={{ width: '40px', height: '40px', zIndex: 99 }}
                        id={`${id}-right`}
                    />
                </>
            )}
        </>
    );
}

export default ModernNode;